import React from 'react'
import { FormikProps } from 'formik'
import { IntlShape } from 'react-intl'
import { TourDetails } from '../../../core/_models'
import { Tooltip, OverlayTrigger } from 'react-bootstrap'
import { KTSVG } from '../../../../../../../_dashboard/helpers'
import {
  buildDetailPayload,
  isContentCategoryType,
} from '../../contentCategoryUtils'

interface IncludeExcludeItem {
  title: string
  description: string
  translations?: Array<{
    language: string
    title: string
    description: string
  }>
}

interface IncludeExcludeProps {
  formik: FormikProps<TourDetails>
  type: 'includes' | 'excludes'
  showForm: boolean
  setShowForm: (show: boolean) => void
  newItem: { title: string; description: string }
  setNewItem: (item: { title: string; description: string }) => void
  handleAddItem?: () => void
  handleDeleteItem?: (index: number) => void
  intl: IntlShape
  patchMutation: any
  contentCategoryId: string | null
  error?: string
  isLoading?: boolean
  selectedLanguage: string
}

const IncludeExclude: React.FC<IncludeExcludeProps> = ({
  formik,
  type,
  showForm,
  setShowForm,
  newItem,
  setNewItem,
  handleAddItem: externalHandleAddItem,
  handleDeleteItem,
  intl,
  patchMutation,
  contentCategoryId,
  error,
  isLoading,
  selectedLanguage
}) => {
  const items = formik.values[type] as IncludeExcludeItem[] | undefined
  const [editIndex, setEditIndex] = React.useState<number | null>(null)
  
  const title = type === 'includes' 
    ? intl.formatMessage({ id: 'TOUR.FIELDS.INCLUDES' })
    : intl.formatMessage({ id: 'TOUR.FIELDS.EXCLUDES' })
  
  const placeholder = type === 'includes'
    ? intl.formatMessage({ id: 'TOUR.FIELDS.ENTER_INCLUDE_TITLE' })
    : intl.formatMessage({ id: 'TOUR.FIELDS.ENTER_EXCLUDE_TITLE' })

  const handleEditClick = (index: number) => {
    if (!items) return
    setEditIndex(index)
    setNewItem(items[index])
  }

  const handleSaveEdit = async () => {
    if (editIndex !== null && items && contentCategoryId) {
      const updatedItems = [...items]
      updatedItems[editIndex] = {
        title: newItem.title.trim(),
        description: newItem.description.trim(),
      }

      const existingCategory = formik.values.contentCategories?.find((item: any) =>
        isContentCategoryType(item.type, type),
      )

      const requestBody = {
        tour: formik.values.id,
        type,
        title: existingCategory?.title || newItem.title.trim(),
        description: existingCategory?.description || newItem.description.trim(),
        details: updatedItems.map((item, index) =>
          buildDetailPayload(
            item,
            index,
            existingCategory?.details,
            selectedLanguage,
          ),
        ),
      }

      await patchMutation.mutateAsync({
        id: contentCategoryId,
        ...requestBody
      })
      
      formik.setFieldValue(type, updatedItems)
      setEditIndex(null)
      setNewItem({ title: '', description: '' })
    }
  }

  const handleAddItem = async () => {
    if (newItem.title.trim() && newItem.description.trim() && contentCategoryId) {
      const updatedItems = [...(items || []), {
        title: newItem.title.trim(),
        description: newItem.description.trim(),
      }]

      const existingCategory = formik.values.contentCategories?.find((item: any) =>
        isContentCategoryType(item.type, type),
      )

      const requestBody = {
        tour: formik.values.id,
        type,
        title: existingCategory?.title || newItem.title.trim(),
        description: existingCategory?.description || newItem.description.trim(),
        details: updatedItems.map((item, index) =>
          buildDetailPayload(
            item,
            index,
            existingCategory?.details,
            selectedLanguage,
          ),
        ),
      }

      await patchMutation.mutateAsync({
        id: contentCategoryId,
        ...requestBody
      })

      formik.setFieldValue(type, updatedItems)
      setNewItem({ title: '', description: '' })
      setShowForm(false)
    }
  }

  const handleAddItemClick = () => {
    if (externalHandleAddItem) {
      externalHandleAddItem()
    } else {
      handleAddItem()
    }
  }

  return (
    <div className='mb-10'>
      <label className='form-label'>{title}</label>
      {error && <div className='text-danger mb-3'>{error}</div>}
      <div className='table-responsive'>
        <table className='table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3'>
          <thead>
            <tr className='fw-bold text-muted'>
              <th className='min-w-150px'>{intl.formatMessage({ id: 'TOUR.FIELDS.TITLE' })}</th>
              <th className='min-w-150px'>{intl.formatMessage({ id: 'TOUR.FIELDS.DESCRIPTION' })}</th>
              <th className='min-w-100px text-end'>{intl.formatMessage({ id: 'COLUMNS.ACTIONS' })}</th>
            </tr>
          </thead>
          <tbody>
            {items?.map((item, index) => (
              <tr key={index}>
                <td className="ps-4">
                  {editIndex === index ? (
                    <input
                      type='text'
                      className='form-control'
                      value={newItem.title}
                      onChange={(e) => setNewItem({...newItem, title: e.target.value})}
                    />
                  ) : (
                    item.title
                  )}
                </td>
                <td className="ps-4">
                  {editIndex === index ? (
                    <input
                      type='text'
                      className='form-control'
                      value={newItem.description}
                      onChange={(e) => setNewItem({...newItem, description: e.target.value})}
                    />
                  ) : (
                    item.description
                  )}
                </td>
                <td className='text-end ps-4'>
                  {editIndex === index ? (
                    <>
                      <button
                        type='button'
                        className='btn btn-light-primary btn-sm me-2'
                        onClick={handleSaveEdit}
                      >
                        {intl.formatMessage({ id: 'TOUR.ACTIONS.SAVE' })}
                      </button>
                      <button
                        type='button'
                        className='btn btn-light-danger btn-sm'
                        onClick={() => {
                          setEditIndex(null)
                          setNewItem({ title: '', description: '' })
                        }}
                      >
                        {intl.formatMessage({ id: 'TOUR.ACTIONS.CANCEL' })}
                      </button>
                    </>
                  ) : (
                    <>
                      <OverlayTrigger
                        placement='top'
                        overlay={<Tooltip>{intl.formatMessage({id: 'ACTION.EDIT'})}</Tooltip>}
                      >
                        <button
                          type='button'
                          className='btn btn-icon btn-light-primary btn-sm me-1'
                          onClick={() => handleEditClick(index)}
                        >
                          <KTSVG path='/media/icons/duotune/art/art005.svg' className='svg-icon-3' />
                        </button>
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement='top'
                        overlay={<Tooltip>{intl.formatMessage({id: 'ACTION.DELETE'})}</Tooltip>}
                      >
                        <button
                          type='button'
                          className='btn btn-icon btn-light-danger btn-sm'
                          onClick={() => {
                            if (handleDeleteItem) {
                              handleDeleteItem(index)
                            } else {
                              const newItems = items.filter((_, i) => i !== index)
                              formik.setFieldValue(type, newItems)
                            }
                          }}
                        >
                          <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-3' />
                        </button>
                      </OverlayTrigger>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {showForm && (
              <tr>
                <td className="ps-4">
                  <input
                    type='text'
                    className='form-control'
                    placeholder={placeholder}
                    value={newItem.title}
                    onChange={(e) => setNewItem({...newItem, title: e.target.value})}
                  />
                </td>
                <td className="ps-4">
                  <input
                    type='text'
                    className='form-control'
                    placeholder={intl.formatMessage({ id: 'TOUR.FIELDS.ENTER_DESCRIPTION' })}
                    value={newItem.description}
                    onChange={(e) => setNewItem({...newItem, description: e.target.value})}
                  />
                </td>
                <td className='text-end ps-4'>
                  <button
                    type='button'
                    className='btn btn-light-primary btn-sm me-2'
                    onClick={handleAddItemClick}
                  >
                    {intl.formatMessage({ id: 'TOUR.ACTIONS.SAVE' })}
                  </button>
                  <button
                    type='button'
                    className='btn btn-light-danger btn-sm'
                    onClick={() => {
                      setShowForm(false)
                      setNewItem({ title: '', description: '' })
                    }}
                  >
                    {intl.formatMessage({ id: 'TOUR.ACTIONS.CANCEL' })}
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {!showForm && (
        <button
          type='button'
          className='btn btn-light-primary'
          onClick={() => setShowForm(true)}
        >
          {intl.formatMessage({ id: 'TOUR.ACTIONS.ADD_ELEMENT' })}
        </button>
      )}
    </div>
  )
}

export { IncludeExclude } 